// src/components/Admin/ContentFormModal.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { X, Save, FileText, Film, Headphones, Palette, Eye, Radio } from 'lucide-react';
import SmartFileUpload from '../Common/SmartFileUpload';
import StreamVideoUpload from './StreamVideoUpload';
import { useStreamEncodingStatus } from '../../hooks/useStreamEncodingStatus';
import type { ContentItem } from '../../services/adminService';

interface ContentFormModalProps {
  isOpen: boolean;
  mode: 'add' | 'edit' | 'view';
  contentItem?: ContentItem;
  onClose: () => void;
  onSave: (formData: FormData) => Promise<void>;
}

const ContentFormModal: React.FC<ContentFormModalProps> = ({
  isOpen,
  mode,
  contentItem,
  onClose,
  onSave,
}) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    contentType: 'CONTENT' as ContentItem['contentType'],
    status: 'DRAFT' as ContentItem['status'],
    category: '',
    ageRating: '' as string,
    bunnyVideoId: '' as string,
    tags: '' as string,
    isFeatured: false,
    isPremium: false,
  });

  const [uploadedFiles, setUploadedFiles] = useState<{
    videoUrl?: string;
    thumbnailUrl?: string;
    videoFileName?: string;
    thumbnailFileName?: string;
    durationSeconds?: number;
    /** Set when the film was uploaded directly to Bunny Stream. */
    bunnyVideoId?: string;
  }>({});
  // Whether Bunny has finished EVERY resolution, not merely made the video playable.
  const [streamEncodingFinished, setStreamEncodingFinished] = useState(false);
  // False once the server reports Stream is not configured; the older uploader is
  // shown instead so creating content still works.
  const [streamAvailable, setStreamAvailable] = useState(true);

  const handleStreamUploaded = useCallback((videoId: string, fileName: string) => {
    setUploadedFiles(prev => ({ ...prev, bunnyVideoId: videoId, videoFileName: fileName }));
  }, []);
  const handleStreamUnavailable = useCallback(() => setStreamAvailable(false), []);

  const [isLoading, setIsLoading] = useState(false);
  // The id as it was when the form opened. Save sends bunnyVideoId only when the
  // admin changed it - so fixing a typo in a title can never quietly un-migrate a
  // film, even if the value somehow failed to prefill.
  const [loadedBunnyVideoId, setLoadedBunnyVideoId] = useState('');

  // For an existing item: has its saved Bunny video finished encoding? Polls the id the
  // item was loaded with rather than the text in the box, so typing a half-pasted id
  // does not fire requests for videos that do not exist.
  const { status: savedEncoding } = useStreamEncodingStatus(
    mode === 'edit' ? loadedBunnyVideoId : null,
    mode === 'edit' && isOpen,
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [uploadInProgress, setUploadInProgress] = useState({
    video: false,
    thumbnail: false
  });

  // Initialize form data when modal opens or contentItem changes
  useEffect(() => {
    if (contentItem && (mode === 'edit' || mode === 'view')) {
      setFormData({
        title: contentItem.title || '',
        description: contentItem.description || '',
        contentType: contentItem.contentType,
        status: contentItem.status,
        category: contentItem.category || '',
        // Prefilled, so editing an already-rated item does not silently blank its
        // rating and make it unpublishable.
        ageRating: contentItem.ageRating || '',
        bunnyVideoId: contentItem.bunnyVideoId || '',
        tags: (contentItem as ContentItem & { tags?: string }).tags || '',
        isFeatured: contentItem.isFeatured || false,
        isPremium: contentItem.isPremium || false,
      });
    } else if (mode === 'add') {
      setFormData({
        title: '',
        description: '',
        contentType: 'CONTENT',
        status: 'DRAFT',
        category: '',
        ageRating: '',
        bunnyVideoId: '',
        tags: '',
        isFeatured: false,
        isPremium: false,
      });
    }
    setLoadedBunnyVideoId((mode === 'edit' || mode === 'view') ? (contentItem?.bunnyVideoId || '') : '');
    setErrors({});
    setStreamEncodingFinished(false);
    setStreamAvailable(true);
    setUploadedFiles({});
    setUploadInProgress({ video: false, thumbnail: false });
  }, [contentItem, mode, isOpen]);

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleVideoUploadComplete = (result: { fileUrl: string; filename: string; durationSeconds?: number }) => {
    setUploadedFiles(prev => ({
      ...prev,
      videoUrl: result.fileUrl,
      videoFileName: result.filename,
      durationSeconds: result.durationSeconds || undefined
    }));
    setUploadInProgress(prev => ({ ...prev, video: false }));
    setErrors(prev => ({ ...prev, video: '' }));
  };

  const handleThumbnailUploadComplete = (result: { fileUrl: string; filename: string }) => {
    setUploadedFiles(prev => ({
      ...prev,
      thumbnailUrl: result.fileUrl,
      thumbnailFileName: result.filename
    }));
    setUploadInProgress(prev => ({ ...prev, thumbnail: false }));
    setErrors(prev => ({ ...prev, thumbnail: '' }));
  };

  const handleUploadError = (type: 'video' | 'thumbnail') => (error: string) => {
    setErrors(prev => ({ ...prev, [type]: error }));
    setUploadInProgress(prev => ({ ...prev, [type]: false }));
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    // The backend refuses this too - this is so the editor finds out before uploading
    // a file rather than after. Unrated means adults-only, so publishing without a
    // rating produces content that appears in listings and refuses every viewer.
    if (formData.status === 'PUBLISHED' && !formData.ageRating) {
      newErrors.ageRating = 'Choose an age rating before publishing, or save as a draft.';
    }

    // Editing an existing streamed item. Outside the add-only block on purpose: placed
    // inside it, `mode === 'edit'` can never be true and this guard silently never runs.
    if (mode === 'edit' && formData.status === 'PUBLISHED' && savedEncoding
        && !savedEncoding.finished && formData.bunnyVideoId.trim() === loadedBunnyVideoId.trim()) {
      newErrors.bunnyVideoId = savedEncoding.failed
        ? 'Bunny could not encode this video. Upload it again before publishing.'
        : 'Bunny is still encoding this video. Keep it as a Draft until encoding finishes.';
    }

    if (mode === 'add') {
      // Video file is required for all content types except LIVE_VIDEO (where it's optional)
      if (mode === 'add' && formData.contentType !== 'LIVE_VIDEO'
          && !uploadedFiles.videoUrl && !uploadedFiles.bunnyVideoId) {
        newErrors.video = 'Video file is required';
      }
      // A film uploaded to Stream is playable at low resolution well before its higher
      // resolutions exist. Publishing then would put it live at 360p. Saving as a draft
      // is always allowed, so a long encode never holds the form open for hours.
      if (uploadedFiles.bunnyVideoId && !streamEncodingFinished && formData.status === 'PUBLISHED') {
        newErrors.video = 'Bunny is still encoding this video. Save it as a Draft now and publish once encoding finishes.';
      }
      if (mode === 'add' && !uploadedFiles.thumbnailUrl) {
        newErrors.thumbnail = 'Thumbnail is required';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (mode === 'view') return;

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    try {
      // Create FormData for file upload
      const submitData = new FormData();
      
      // Add text fields
      submitData.append('title', formData.title);
      submitData.append('description', formData.description);
      submitData.append('contentType', formData.contentType);
      submitData.append('status', formData.status);
      submitData.append('category', formData.category);
      submitData.append('isFeatured', formData.isFeatured.toString());
      submitData.append('isPremium', formData.isPremium.toString());
      if (formData.ageRating) {
        submitData.append('ageRating', formData.ageRating);
      }
      // Sent only when changed. An empty string is meaningful - it reverts the item to
      // its original file - so it must be sent when the admin cleared the box, and must
      // NOT be sent just because the box happened to be empty.
      if (mode === 'edit' && formData.bunnyVideoId.trim() !== loadedBunnyVideoId.trim()) {
        submitData.append('bunnyVideoId', formData.bunnyVideoId.trim());
      }
      if (formData.tags.trim()) {
        submitData.append('tags', formData.tags.trim());
      }

      // Add file URLs if present
      if (uploadedFiles.bunnyVideoId) {
        submitData.append('bunnyVideoId', uploadedFiles.bunnyVideoId);
      }
      if (uploadedFiles.videoUrl) {
        submitData.append('videoUrl', uploadedFiles.videoUrl);
        if (uploadedFiles.videoFileName) {
          submitData.append('videoFileName', uploadedFiles.videoFileName);
        }
      }
      if (uploadedFiles.durationSeconds) {
        submitData.append('durationSeconds', uploadedFiles.durationSeconds.toString());
      }
      if (uploadedFiles.thumbnailUrl) {
        submitData.append('thumbnailUrl', uploadedFiles.thumbnailUrl);
        if (uploadedFiles.thumbnailFileName) {
          submitData.append('thumbnailFileName', uploadedFiles.thumbnailFileName);
        }
      }

      // Add content ID for edit mode
      if (mode === 'edit' && contentItem) {
        submitData.append('id', contentItem.id);
      }

      await onSave(submitData);
    } finally {
      setIsLoading(false);
    }
  };

  const contentTypeOptions = [
    { value: 'CINEMA', label: 'Cinema', icon: Film, description: 'Movies and cinematic content' },
    { value: 'CONTENT', label: 'Content', icon: FileText, description: 'General content and articles' },
    { value: 'PODCAST', label: 'Podcast', icon: Headphones, description: 'Audio podcast episodes' },
    { value: 'ANIMATION', label: 'Animation', icon: Palette, description: 'Animated videos and content' },
    { value: 'SNEAK_PEEK', label: 'Sneak Peek', icon: Eye, description: 'Preview and teaser content' },
    { value: 'LIVE_VIDEO', label: 'Live Video', icon: Radio, description: 'Live streaming content' },
  ];

  const statusOptions = [
    { value: 'DRAFT', label: 'Draft', description: 'Not published yet' },
    { value: 'PUBLISHED', label: 'Published', description: 'Live and visible to users' },
    { value: 'ARCHIVED', label: 'Archived', description: 'Hidden from public view' },
    { value: 'SCHEDULED', label: 'Scheduled', description: 'Scheduled for future publication' },
  ];

  // BBFC vocabulary, because the audience is British and already reads these.
  //
  // The blank option is listed first and says what it means. Leaving it blank is a
  // real choice with a real consequence - unrated content is treated as adults-only,
  // so it becomes invisible to everybody - and a form that left that unsaid is how
  // the entire catalogue went dark once already.
  const ageRatingOptions = [
    { value: '', label: 'Not rated yet', description: 'Drafts only. Unrated content is treated as adults-only and cannot be published.' },
    { value: 'U', label: 'U', description: 'Suitable for all' },
    { value: 'PG', label: 'PG', description: 'Parental guidance' },
    { value: '12', label: '12', description: 'Not suitable for under 12s' },
    { value: '15', label: '15', description: 'Not suitable for under 15s' },
    { value: '18', label: '18', description: 'Adults only' },
  ];

  const getModalTitle = () => {
    switch (mode) {
      case 'add':
        return 'Create New Content';
      case 'edit':
        return `Edit: ${contentItem?.title || 'Content'}`;
      case 'view':
        return `View: ${contentItem?.title || 'Content'}`;
      default:
        return 'Content Form';
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
    >
      <div 
        className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">
              {getModalTitle()}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-1 rounded-full transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Title *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              disabled={mode === 'view'}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent disabled:bg-gray-100"
              placeholder="Enter content title"
            />
            {errors.title && (
              <p className="mt-1 text-sm text-red-600">{errors.title}</p>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              disabled={mode === 'view'}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent disabled:bg-gray-100"
              placeholder="Enter content description"
            />
          </div>

          {/* Content Type and Status */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Content Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Content Type *
              </label>
              <select
                value={formData.contentType}
                onChange={(e) => handleInputChange('contentType', e.target.value)}
                disabled={mode === 'view'}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent disabled:bg-gray-100"
              >
                {contentTypeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-sm text-gray-500">
                {contentTypeOptions.find(opt => opt.value === formData.contentType)?.description}
              </p>
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status *
              </label>
              <select
                value={formData.status}
                onChange={(e) => handleInputChange('status', e.target.value)}
                disabled={mode === 'view'}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent disabled:bg-gray-100"
              >
                {statusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-sm text-gray-500">
                {statusOptions.find(opt => opt.value === formData.status)?.description}
              </p>
            </div>

            {/* Age rating */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Age rating {formData.status === 'PUBLISHED' && '*'}
              </label>
              <select
                value={formData.ageRating}
                onChange={(e) => handleInputChange('ageRating', e.target.value)}
                disabled={mode === 'view'}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent disabled:bg-gray-100"
              >
                {ageRatingOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {errors.ageRating ? (
                <p className="mt-1 text-sm text-red-600">{errors.ageRating}</p>
              ) : (
                <p className="mt-1 text-sm text-gray-500">
                  {ageRatingOptions.find(opt => opt.value === formData.ageRating)?.description}
                </p>
              )}
            </div>

            {/* Bunny Stream video - edit only. New uploads go to Stream automatically;
                this is for attaching a film that was uploaded through the Bunny
                dashboard, which is how large files have to be uploaded. */}
            {mode !== 'add' && (
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bunny Stream video ID
                </label>
                <input
                  type="text"
                  value={formData.bunnyVideoId}
                  onChange={(e) => handleInputChange('bunnyVideoId', e.target.value)}
                  disabled={mode === 'view'}
                  placeholder="e.g. 9f1a2b3c-4d5e-6f70-8192-a3b4c5d6e7f8"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-orange-500 focus:border-transparent disabled:bg-gray-100"
                />
                {errors.bunnyVideoId ? (
                  <p className="mt-1 text-sm text-red-600">{errors.bunnyVideoId}</p>
                ) : savedEncoding && formData.bunnyVideoId.trim() === loadedBunnyVideoId.trim() ? (
                  <p className={`mt-1 text-sm ${savedEncoding.failed ? 'text-red-600' : savedEncoding.finished ? 'text-green-700' : 'text-blue-700'}`}>
                    {savedEncoding.failed && 'Encoding failed in Bunny.'}
                    {savedEncoding.finished && `Encoding finished${savedEncoding.availableResolutions ? ` - ${savedEncoding.availableResolutions}` : ''}. Safe to publish.`}
                    {!savedEncoding.failed && !savedEncoding.finished && (
                      `Still encoding${savedEncoding.encodeProgress != null ? ` (${savedEncoding.encodeProgress}%)` : ''}${savedEncoding.availableResolutions ? ` - so far ${savedEncoding.availableResolutions}` : ''}.`
                    )}
                  </p>
                ) : (
                  <p className="mt-1 text-sm text-gray-500">
                    {formData.bunnyVideoId.trim()
                      ? 'Streams with adaptive quality from Bunny. Wait until the video shows Finished in the library before saving.'
                      : 'Empty: plays the original uploaded file. Paste the Video ID from the Bunny library to switch to adaptive streaming. Clearing this later switches back.'}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Category
            </label>
            <input
              type="text"
              value={formData.category}
              onChange={(e) => handleInputChange('category', e.target.value)}
              disabled={mode === 'view'}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent disabled:bg-gray-100"
              placeholder="Enter category (optional)"
            />
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tags
            </label>
            <input
              type="text"
              value={formData.tags}
              onChange={(e) => handleInputChange('tags', e.target.value)}
              disabled={mode === 'view'}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent disabled:bg-gray-100"
              placeholder="Enter tags separated by commas (e.g. action, thriller, drama)"
            />
            <p className="mt-1 text-sm text-gray-500">
              Separate multiple tags with commas. Leave empty for default tags based on category and content type.
            </p>
          </div>

          {/* File Uploads - Only show for add/edit modes */}
          {mode !== 'view' && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Media Files</h3>
              
              {/* Video File */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {formData.contentType === 'LIVE_VIDEO' 
                    ? 'Video File (optional - for pre-recorded content)'
                    : `Video File ${mode === 'add' ? '*' : '(optional)'}`
                  }
                </label>
                
                {uploadedFiles.videoUrl ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-green-800">
                          ✅ {uploadedFiles.videoFileName || 'Video uploaded successfully'}
                        </p>
                        <p className="text-xs text-green-600 mt-1">
                          Video is ready and will be used when saving content
                        </p>
                      </div>
                      <button
                        onClick={() => setUploadedFiles(prev => ({ ...prev, videoUrl: undefined, videoFileName: undefined }))}
                        className="text-green-600 hover:text-green-800 text-sm underline"
                        type="button"
                      >
                        Replace
                      </button>
                    </div>
                  </div>
                ) : mode === 'add' && streamAvailable && formData.contentType !== 'PODCAST' ? (
                  // Films go straight to Bunny Stream for adaptive streaming. Podcasts
                  // keep the existing uploader because they are frequently audio-only,
                  // which Stream is not for.
                  <StreamVideoUpload
                    title={formData.title}
                    disabled={mode !== 'add'}
                    onUploaded={handleStreamUploaded}
                    onEncodingFinished={setStreamEncodingFinished}
                    onUnavailable={handleStreamUnavailable}
                  />
                ) : (
                  <SmartFileUpload
                    onUploadComplete={handleVideoUploadComplete}
                    onUploadError={handleUploadError('video')}
                    acceptedTypes={['video/*', 'audio/*']}
                    maxFileSize={10 * 1024 * 1024 * 1024} // 10GB
                    chunkThreshold={10 * 1024 * 1024} // 10MB - matches backend limit
                    disabled={mode === 'view' || false}
                  />
                )}
                
                {errors.video && (
                  <p className="mt-1 text-sm text-red-600">{errors.video}</p>
                )}
                {formData.contentType === 'LIVE_VIDEO' && (
                  <p className="mt-2 text-sm text-gray-500">
                    💡 For live videos, you can upload a pre-recorded video file or leave empty for real-time streaming
                  </p>
                )}
              </div>

              {/* Thumbnail */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Thumbnail {mode === 'add' ? '*' : '(optional)'}
                </label>
                
                {uploadedFiles.thumbnailUrl ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-green-800">
                          ✅ {uploadedFiles.thumbnailFileName || 'Thumbnail uploaded successfully'}
                        </p>
                        <p className="text-xs text-green-600 mt-1">
                          Thumbnail is ready and will be used when saving content
                        </p>
                      </div>
                      <button
                        onClick={() => setUploadedFiles(prev => ({ ...prev, thumbnailUrl: undefined, thumbnailFileName: undefined }))}
                        className="text-green-600 hover:text-green-800 text-sm underline"
                        type="button"
                      >
                        Replace
                      </button>
                    </div>
                  </div>
                ) : (
                  <SmartFileUpload
                    onUploadComplete={handleThumbnailUploadComplete}
                    onUploadError={handleUploadError('thumbnail')}
                    acceptedTypes={['image/*']}
                    maxFileSize={10 * 1024 * 1024} // 10MB for thumbnails
                    chunkThreshold={5 * 1024 * 1024} // 5MB threshold for images
                    disabled={mode === 'view' || false}
                  />
                )}
                
                {errors.thumbnail && (
                  <p className="mt-1 text-sm text-red-600">{errors.thumbnail}</p>
                )}
              </div>
            </div>
          )}

          {/* Options */}
          <div className="border-t pt-4">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Options</h3>
            <div className="flex items-center space-x-6">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.isFeatured}
                  onChange={(e) => handleInputChange('isFeatured', e.target.checked)}
                  disabled={mode === 'view'}
                  className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                />
                <span className="ml-2 text-sm text-gray-700">Featured Content</span>
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.isPremium}
                  onChange={(e) => handleInputChange('isPremium', e.target.checked)}
                  disabled={mode === 'view'}
                  className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                />
                <span className="ml-2 text-sm text-gray-700">Premium Content</span>
              </label>
            </div>
          </div>

          {/* Show existing content info for view/edit mode */}
          {(mode === 'edit' || mode === 'view') && contentItem && (
            <div className="bg-gray-50 rounded-lg p-4 border-t">
              <h3 className="text-sm font-medium text-gray-900 mb-3">Content Information</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-gray-700">Author: </span>
                  <span className="text-gray-600">{contentItem.author.username}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Views: </span>
                  <span className="text-gray-600">{contentItem.viewCount?.toLocaleString() || '0'}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Likes: </span>
                  <span className="text-gray-600">{contentItem.likeCount?.toLocaleString() || '0'}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Created: </span>
                  <span className="text-gray-600">
                    {new Date(contentItem.createdAt).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4">
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-orange-500 disabled:opacity-50"
            >
              {mode === 'view' ? 'Close' : 'Cancel'}
            </button>
            {mode !== 'view' && (
              <button
                type="submit"
                onClick={handleSubmit}
                disabled={isLoading || !formData.title.trim()}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 focus:ring-2 focus:ring-orange-500 disabled:opacity-50 flex items-center"
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    {mode === 'add' ? 'Create Content' : 'Save Changes'}
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContentFormModal;